import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import {
  createEditHistory,
  type EditCaret,
  type EditDraft,
  type EditKind,
  editKindForInputType,
  type EditState,
  recordEdit,
  redoEdit,
  undoEdit,
} from '../utils/editHistory';

type CaretReading = { caret: EditCaret; length: number };

type PendingEdit = { kind: EditKind | null; caretBefore: EditCaret | null };

function editCaretPart(part: string | null): EditCaret['part'] {
  switch (part) {
    case 'key':
    case 'op':
    case 'value':
    case 'operator':
    case 'type':
      return part;
    default:
      return null;
  }
}

function sameCaretCell(a: EditCaret | null, b: EditCaret | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.key === b.key && a.part === b.part;
}

function readCaret(container: HTMLElement | null): CaretReading | null {
  const active = document.activeElement;
  if (!container || !(active instanceof HTMLInputElement) || !container.contains(active)) {
    return null;
  }
  const part = editCaretPart(active.getAttribute('data-part'));
  const key =
    active.closest<HTMLElement>('[data-token-container][data-key]')?.getAttribute('data-key') ??
    active.getAttribute('data-key');
  if (!key) {
    return null;
  }
  return {
    caret: { key, part, offset: active.selectionStart ?? active.value.length },
    length: active.value.length,
  };
}

function readDrafts(container: HTMLElement | null): EditDraft[] {
  if (!container) {
    return [];
  }
  const drafts: EditDraft[] = [];
  container.querySelectorAll<HTMLInputElement>('input[data-freetext-token][data-key]').forEach((input) => {
    const key = input.getAttribute('data-key');
    const committed = input.getAttribute('data-committed-value') ?? '';
    if (key && input.value !== committed) {
      drafts.push({ key, text: input.value });
    }
  });
  return drafts;
}

export function useUndoStack({
  query,
  containerRef,
  enabled,
  onRestore,
}: {
  query: string;
  containerRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  onRestore: (state: EditState, caret: EditCaret | null) => void;
}): { undo: () => void; redo: () => void } {
  const historyRef = useRef(createEditHistory({ query, drafts: [] }));
  const queryRef = useRef(query);
  const lastCaretRef = useRef<CaretReading | null>(null);
  const beforeInputCaretRef = useRef<CaretReading | null>(null);
  const runStartCaretRef = useRef<EditCaret | null>(null);
  const pendingRef = useRef<PendingEdit | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoringRef = useRef(false);
  const onRestoreRef = useRef(onRestore);
  useLayoutEffect(() => {
    queryRef.current = query;
    onRestoreRef.current = onRestore;
  }, [onRestore, query]);

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || restoringRef.current) {
      return;
    }
    const after = readCaret(containerRef.current);
    const cellChanged = !sameCaretCell(pending.caretBefore, after?.caret ?? null);
    const kind = cellChanged ? 'other' : (pending.kind ?? 'other');
    const caretBefore = cellChanged ? pending.caretBefore : (runStartCaretRef.current ?? pending.caretBefore);
    const next = recordEdit(historyRef.current, {
      state: { query: queryRef.current, drafts: readDrafts(containerRef.current) },
      kind,
      caretBefore,
      caretAfter: after?.caret ?? null,
    });
    if (next === historyRef.current && kind !== 'other') {
      runStartCaretRef.current = caretBefore;
    } else {
      runStartCaretRef.current = null;
    }
    historyRef.current = next;
    lastCaretRef.current = after;
  }, [containerRef]);

  const schedule = useCallback(
    (pending: PendingEdit) => {
      if (!pendingRef.current) {
        pendingRef.current = pending;
      }
      if (timerRef.current === null) {
        timerRef.current = setTimeout(flush, 0);
      }
    },
    [flush],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
      return undefined;
    }
    const noteCaret = () => {
      lastCaretRef.current = readCaret(container);
    };
    const onBeforeInput = () => {
      beforeInputCaretRef.current = readCaret(container);
    };
    const onInput = (e: Event) => {
      if (restoringRef.current) {
        return;
      }
      const before = beforeInputCaretRef.current ?? lastCaretRef.current;
      beforeInputCaretRef.current = null;
      const target = e.target instanceof HTMLInputElement ? e.target : null;
      const inputType = e instanceof InputEvent ? e.inputType : undefined;
      const grew = target ? target.value.length >= (before?.length ?? 0) : true;
      if (pendingRef.current) {
        flush();
      }
      schedule({ kind: editKindForInputType(inputType, grew), caretBefore: before?.caret ?? null });
    };
    container.addEventListener('keydown', noteCaret, true);
    container.addEventListener('pointerdown', noteCaret, true);
    container.addEventListener('beforeinput', onBeforeInput, true);
    container.addEventListener('input', onInput, true);
    return () => {
      container.removeEventListener('keydown', noteCaret, true);
      container.removeEventListener('pointerdown', noteCaret, true);
      container.removeEventListener('beforeinput', onBeforeInput, true);
      container.removeEventListener('input', onInput, true);
    };
  }, [containerRef, enabled, flush, schedule]);

  useEffect(() => {
    if (restoringRef.current || !enabled) {
      return;
    }
    queryRef.current = query;
    schedule({ kind: null, caretBefore: lastCaretRef.current?.caret ?? null });
  }, [query, enabled, schedule]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const restore = useCallback((step: ReturnType<typeof undoEdit>) => {
    if (!step) {
      return;
    }
    historyRef.current = step.history;
    restoringRef.current = true;
    lastCaretRef.current = null;
    runStartCaretRef.current = null;
    onRestoreRef.current(step.restore.state, step.restore.caret);
    setTimeout(() => {
      restoringRef.current = false;
    }, 0);
  }, []);

  const undo = useCallback(() => {
    flush();
    restore(undoEdit(historyRef.current));
  }, [flush, restore]);

  const redo = useCallback(() => {
    flush();
    restore(redoEdit(historyRef.current));
  }, [flush, restore]);

  return { undo, redo };
}
